import React from "react";
import { CalendarDays, Eye, MapPin } from "lucide-react";

import { concordText, countText, dayList, hasPlaceMap, LIST_SECTIONS, rowCells, truckOrderNotes } from "../../utils/dispatchGeotabOrders.js";
import StatusBadge from "../geotab/GeotabOrderStatusBadge.jsx";

// Liste de la fenêtre Geotab du Dispatch, claire comme les autres fenêtres du SaaS : une ligne par écart, rangée selon son
// sens (prévu au Dispatch, pas vu par Geotab ; vu par Geotab, absent du Dispatch), avec ce qui s'est passé et une piste.
// « Voir » passe au lecteur du camion, qui reste foncé. Le Comparatif de Rapports reprend les mêmes lignes, datées.

// Bannière de chaque sens : vert du SaaS pour le Dispatch, bleu Geotab pour Geotab.
const SECTION_BANNERS = {
  planned: { className: "bg-accent", Icon: CalendarDays },
  seen: { className: "bg-[#25477b]", Icon: MapPin },
};

const tons = (value) => Number(value || 0).toLocaleString("fr-CA", { maximumFractionDigits: 1 });

// dated : date de la commande dans le Comparatif. « Carte » ouvre la lecture guidée dans la fenêtre du Dispatch.
export function Rows({ rows, date = "", dated = false, canOpen = () => false, onOpen, onOpenPlace }) {
  return (
    <table className="w-full">
      <tbody>
        {rows.map((row, at) => {
          const cells = rowCells(row, date);
          const key = row.order?.id || `${row.kind}:${at}`;
          const mapped = Boolean(onOpenPlace) && hasPlaceMap(row);
          return (
            <React.Fragment key={key}>
              <tr data-geotab-row={row.kind}>
                <td className="w-[118px]"><StatusBadge status={row.kind} /></td>
                {dated ? <td className="w-[84px] whitespace-nowrap tabular-nums text-slate-600">{row.order?.date}</td> : null}
                <td className="w-[230px] font-semibold">{cells.place}{cells.sub ? <div className="text-[0.72rem] font-normal text-slate-500">{cells.sub}</div> : null}</td>
                <td className="w-[76px] text-slate-700">{cells.product}</td>
                <td className="w-[36px] text-right tabular-nums">{cells.tons == null ? "" : tons(cells.tons)}</td>
                <td>{cells.text}{cells.hint ? <b className="ml-1 whitespace-nowrap font-semibold text-[#8a4b12]">{cells.hint}</b> : null}</td>
                <td className="w-[130px] whitespace-nowrap text-right">
                  {row.truck ? <b>{row.truck}</b> : <span className="text-slate-500">Sur aucun camion</span>}
                  {row.driver ? <div className="text-[0.72rem] text-slate-500">{row.driver}</div> : null}
                </td>
                {onOpenPlace ? <td className="w-[72px] text-right">
                  {mapped ? (
                    <button type="button" aria-haspopup="dialog" onClick={(event) => onOpenPlace(row.order, event.currentTarget)} data-geotab-place-toggle={key}
                      className="inline-flex items-center gap-1 whitespace-nowrap rounded-[4px] border border-[#3f5585] bg-white px-2 py-0.5 text-[0.76rem] font-bold text-[#3f5585] focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent">
                      <MapPin aria-hidden="true" className="h-3.5 w-3.5" strokeWidth={2.2} />Carte
                    </button>
                  ) : null}
                </td> : null}
                {onOpen ? <td className="w-[64px] text-right">
                  {canOpen(row.truckId) ? (
                    <button type="button" onClick={() => onOpen(row.truckId)} data-geotab-orders-open={row.truckId}
                      className="inline-flex items-center gap-1 whitespace-nowrap rounded-[4px] border border-[#3f5585] bg-[#3f5585] px-2 py-0.5 text-[0.76rem] font-bold text-white focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent">
                      <Eye aria-hidden="true" className="h-3.5 w-3.5" strokeWidth={2.2} />Voir
                    </button>
                  ) : null}
                </td> : null}
              </tr>
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
}

export function DispatchGeotabOrdersPanel({ data, gaps, date, onOpen, onOpenPlace, saved = "" }) {
  const [showFound, setShowFound] = React.useState(false);
  const list = React.useMemo(() => dayList(data), [data]);
  const gapTruckIds = new Set(gaps.map((gap) => gap.truck_id));
  const withDay = new Set((data?.days || []).map((day) => day.truck_id));
  const canOpen = (truckId) => Boolean(truckId) && (gapTruckIds.has(truckId) || withDay.has(truckId));
  const sections = LIST_SECTIONS.filter((section) => list[section.key].length);
  return (
    <div className="gg-orders px-4 pb-3 pt-2.5 text-[0.82rem] text-slate-900" data-geotab-orders-panel="true">
      {/* Commandes illisibles : les écarts des camions restent listés. */}
      {data?.in_progress ? <p className="m-0 mb-1 text-slate-600">Journée en cours : seuls les écarts déjà constatés sont affichés.</p>
        : list.ordersRead ? null : <p className="m-0 mb-1 text-[0.86rem] font-semibold text-[#9f2f27]" role="alert">Les commandes n’ont pas pu être comparées à Geotab.</p>}
      <p className="m-0 text-[0.95rem] font-bold">
        {list.count ? countText(list) : "Aucun écart entre le Dispatch et Geotab."}
        {concordText(list) ? <span className="font-normal text-slate-500"> · {concordText(list)}</span> : null}
      </p>
      {saved ? <p className="m-0 mt-1 font-semibold text-accent" role="status" data-geotab-place-saved="true">{saved}</p> : null}
      {sections.length ? (
        <div className="mt-2 overflow-x-auto rounded-[4px] border border-slate-300">
          {sections.map((section) => {
            const { className, Icon } = SECTION_BANNERS[section.key];
            return (
              <section key={section.key} data-geotab-orders-section={section.key}>
                <h3 className={`m-0 flex flex-wrap items-center gap-2 px-2.5 py-2 text-[0.72rem] font-extrabold uppercase tracking-[0.07em] text-white ${className}`}>
                  <Icon aria-hidden="true" className="h-[18px] w-[18px] shrink-0" />
                  <span className="inline-flex h-[18px] min-w-[20px] items-center justify-center rounded-[4px] bg-white px-1 text-slate-900">{list[section.key].length}</span>
                  {section.title}
                  <span className="text-[0.76rem] font-medium normal-case tracking-normal text-white/85">— {section.why}</span>
                </h3>
                <Rows rows={list[section.key]} date={date} canOpen={canOpen} onOpen={onOpen} onOpenPlace={onOpenPlace} />
              </section>
            );
          })}
        </div>
      ) : null}
      {list.found.length ? (
        <>
          <button type="button" aria-expanded={showFound} onClick={() => setShowFound((value) => !value)}
            className="mt-2 flex w-full items-center gap-2 rounded-[4px] border border-dashed border-slate-300 bg-transparent px-2.5 py-1 text-left text-slate-500 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent">
            <span aria-hidden="true">{showFound ? "▾" : "▸"}</span>
            <span><b className="text-accent">✓ {list.found.length} commande{list.found.length > 1 ? "s" : ""} concord{list.found.length > 1 ? "ent" : "e"}</b> : le bon camion s’est arrêté chez le client · {showFound ? "Masquer" : "Afficher"}</span>
          </button>
          {showFound ? <div className="mt-1 overflow-x-auto rounded-[4px] border border-slate-300"><Rows rows={list.found} date={date} canOpen={canOpen} onOpen={onOpen} onOpenPlace={onOpenPlace} /></div> : null}
        </>
      ) : null}
      <p className="mb-0 mt-1.5 text-[0.76rem] text-slate-500">Geotab ne confirme pas une livraison : il montre qu’un camion s’est arrêté chez le client.</p>
    </div>
  );
}

// Au-dessus du lecteur : le retour à la liste et les commandes douteuses du camion.
export function DispatchGeotabOrdersBanner({ orders, truckName, onBack }) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-[#2b3546] bg-[#1a2230] px-4 py-1.5 text-[0.8rem] text-[#c3cbd8]" data-geotab-orders-banner="true">
      <button type="button" className="gg-btn" onClick={onBack}>‹ Liste</button>
      {truckOrderNotes(orders, truckName).map(({ status, clients }) => (
        <span key={status} className="inline-flex flex-wrap items-center gap-x-1.5 gap-y-1">
          <StatusBadge status={status} />
          {clients.map(([client, ids], at) => <span key={client}>{at ? "· " : ""}<b className="text-[#e6eaf0]">{client}</b> ({ids.join(", ")})</span>)}
        </span>
      ))}
    </div>
  );
}
